import { Button, useTranslation } from '@packages/utils';
import { RoutesPaths } from 'routes/Routes.types';
import { useCheckPermission } from 'hooks/useCheckPermission';
import { useNavigate, useParams } from 'react-router-dom';
import { Lightning, ActionPlan } from 'assets/icon/icon';
import styled from 'styled-components';
import { useGetElcByIdQuery } from 'redux/apiSlice/elcs.slice';
import { IBreadcrumbItem } from 'components/Library/Breadcrumb/BreadcrumbExports';
import { useAppSelector } from 'redux/store';
import { EUserType } from 'data/types/user.types';
import { useIsFeatureEnabled } from 'hooks/useIsFeatureEnabled';
import ELCDetail from '../ELCDetail';

const StyledButton = styled(Button)`
  && svg {
    transition: fill 0.2s ease, stroke 0.2s ease;
  }

  &&:hover svg path {
    fill: #3ba1b8;
    stroke: #3ba1b8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const AllELCDetail = () => {
  const { t } = useTranslation({
    keyPrefix: 'descriptions.admin-portal.elcs.list.all-elcs',
  });
  const navigate = useNavigate();
  const params = useParams();
  const { id } = params;
  const userData = useAppSelector(state => state.userSlice.user);
  const isLead = userData?.type?.includes(EUserType.ELC_LEAD);
  const { data } = useGetElcByIdQuery({
    elcId: id as string,
  });

  const isEditable = useCheckPermission('ELC_DETAILS_VIEW');
  const hasPillarsViewAccess = useCheckPermission('PILLARS_VIEW');
  const isPillarsManagementEnabled = useIsFeatureEnabled('pillars_management');

  const navigateToELCs = () => {
    navigate(`/app/${RoutesPaths.ELCS}`);
  };

  const navigateToPillars = () => {
    if (!id) {
      console.error(
        'Cannot navigate to PillarsOfStrength: Missing ID parameter',
      );
      return;
    }
    const routePath = RoutesPaths.ELC_PILLARS_OF_STRENGTH;
    const pillarsPath = `/app/${routePath}`
      .replace(':id', id)
      .replace(':pillarId', id);
    navigate(pillarsPath);
  };

  const handleActionPlanClick = () => {
    if (!id) {
      console.error('Cannot navigate to Action Plan: Missing ID parameter');
      return;
    }
    const actionPlanPath = `/app/${RoutesPaths.ELC_ACTION_PLAN.replace(
      ':id',
      id,
    )}`;
    navigate(actionPlanPath);
  };

  const pillarsButton =
    isLead && hasPillarsViewAccess ? (
      <StyledButton
        key="pillars-button"
        onClick={navigateToPillars}
        color="primary"
        icon={<Lightning size={14} />}
        data-cy="pillars_btn"
      >
        Pillars
      </StyledButton>
    ) : undefined;

  const actionPlanButton =
    isLead && isPillarsManagementEnabled ? (
      <StyledButton
        key="action-plan-button"
        onClick={handleActionPlanClick}
        color="primary"
        icon={<ActionPlan size={14} />}
        data-cy="action_plan_btn"
      >
        Action Plan
      </StyledButton>
    ) : undefined;

  const extraActions = (
    <ButtonGroup>
      {pillarsButton}
      {actionPlanButton}
    </ButtonGroup>
  );

  const breadcrumbItems: IBreadcrumbItem[] = [
    {
      label: t('TITLE'),
      onClick: navigateToELCs,
    },
    {
      label: data?.name || '',
    },
  ];

  return (
    <ELCDetail
      isDetailsEditable={!isEditable}
      backUrl={`/app/${RoutesPaths.ELCS}`}
      backButtonSuffix={t('TITLE')}
      extraActions={extraActions}
      breadcrumbItems={breadcrumbItems}
    />
  );
};

export default AllELCDetail;
